import { ArrowLeft, ShieldHalf, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Неверный логин или пароль");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 shadow-inner focus:border-indigo-300 focus:outline-none focus:ring-4 focus:ring-indigo-200/60";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col justify-center px-6 py-12">
        <div className="mb-8 flex items-center justify-between text-sm text-slate-500">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm transition hover:bg-slate-50"
          >
            <ArrowLeft size={18} />
            На главную
          </Link>
          <span className="text-xs uppercase tracking-[0.35em] text-slate-400">TravelStat</span>
        </div>

        <div className="grid gap-8 rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_25px_60px_rgba(15,23,42,0.08)] backdrop-blur md:grid-cols-2">
          <div className="space-y-5">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner">
              <ShieldHalf size={26} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Войти в TravelStat</h1>
              <p className="mt-2 text-sm text-slate-500">
                Управляйте объектами размещения, следите за аналитикой и сравнивайте показатели с конкурентами.
              </p>
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              {[
                "Отчеты по платформам, категориям и трендам",
                "Доступ к списку объектов и конкурентов",
                "Обновления данных в реальном времени",
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Sparkles size={16} className="mt-[2px] text-indigo-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-6 shadow-inner">
            <h2 className="text-lg font-semibold text-slate-900">Данные для входа</h2>
            {error && (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <input
                className={inputClass}
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
              <input
                className={inputClass}
                type="password"
                placeholder="Пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-110 disabled:opacity-60"
              >
                {submitting ? "Входим…" : "Войти"}
              </button>
            </form>
            <div className="mt-4 flex flex-col items-center gap-2 text-sm">
              <Link to="/auth/forgot-password" className="font-medium text-indigo-600 hover:text-indigo-700">
                Забыли пароль?
              </Link>
              <p className="text-xs text-slate-400">
                Нет аккаунта?{" "}
                <Link to="/auth/register" className="font-medium text-indigo-600 hover:text-indigo-700">
                  Зарегистрироваться
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
